import { useState, useEffect, useRef, createContext, useContext } from 'react'
import { Link } from 'react-router-dom'
import { LandingPricing } from '../components/landing/LandingPricing'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import {
  Menu, X, Star, Check, ArrowRight, BarChart3, Users, FileText,
  CreditCard, Package, Building2, Globe, TrendingUp, TrendingDown,
  Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram,
  ClipboardList, ChevronDown, Layers, Zap, Lock, Sun, Moon,
  Bell, ChevronRight, AlertCircle, CheckCircle2, XCircle,
  Calendar, DollarSign, Activity, Clock,
} from 'lucide-react'

/* ─── Theme ────────────────────────────────────────────── */
interface TC {
  accent: string; accentLight: string; accentDark: string; accentGrad: string
  bg0: string; bg1: string; bg2: string; bg3: string
  textPrimary: string; textMuted: string; border: string
  shadowAccent: string; glowBg: string; isDark: boolean
}
const dark: TC = {
  accent:'#6366f1', accentLight:'#818cf8', accentDark:'#4f46e5',
  accentGrad:'linear-gradient(135deg,#6366f1,#4f46e5)',
  bg0:'#070910', bg1:'#0c0e1a', bg2:'#11141f', bg3:'#171b28',
  textPrimary:'#eef0fc', textMuted:'#7880a0',
  border:'rgba(255,255,255,0.07)', shadowAccent:'rgba(99,102,241,0.4)',
  glowBg:'rgba(99,102,241,0.13)', isDark:true,
}
const light: TC = {
  accent:'#4f46e5', accentLight:'#6366f1', accentDark:'#4338ca',
  accentGrad:'linear-gradient(135deg,#6366f1,#4338ca)',
  bg0:'#f0f2f8', bg1:'#ffffff', bg2:'#f5f6fc', bg3:'#eceef7',
  textPrimary:'#0f172a', textMuted:'#64748b',
  border:'rgba(0,0,0,0.09)', shadowAccent:'rgba(79,70,229,0.28)',
  glowBg:'rgba(99,102,241,0.09)', isDark:false,
}
const Ctx = createContext<{ t: TC; toggle: ()=>void }>({ t: dark, toggle: ()=>{} })
const useT = () => useContext(Ctx)

/* ─── Scroll Y ─────────────────────────────────────────── */
function useScrollY() {
  const [y, setY] = useState(0)
  useEffect(() => {
    const h = () => setY(window.scrollY)
    window.addEventListener('scroll', h, { passive: true })
    return () => window.removeEventListener('scroll', h)
  }, [])
  return y
}

/* ─── Animated counter ────────────────────────────────── */
function Counter({ end, prefix='', suffix='', dur=2 }: { end:number; prefix?:string; suffix?:string; dur?:number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref as React.RefObject<Element>, { once: true })
  const [v, setV] = useState(0)
  useEffect(() => {
    if (!inView) return
    const t0 = Date.now()
    const tick = () => {
      const p = Math.min((Date.now()-t0)/(dur*1000), 1)
      setV(Math.round((1-(1-p)**3)*end))
      if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [inView, end, dur])
  return <span ref={ref}>{prefix}{v.toLocaleString()}{suffix}</span>
}

/* ─── AnimatedSection ──────────────────────────────────── */
function AnimatedSection({ children, className='', delay=0, style }: {
  children: React.ReactNode; className?: string; delay?: number; style?: React.CSSProperties
}) {
  const ref = useRef(null)
  const inView = useInView(ref as React.RefObject<Element>, { once: true, margin:'-70px' })
  return (
    <motion.div ref={ref} initial={{ opacity:0, y:28 }}
      animate={inView ? { opacity:1, y:0 } : {}}
      transition={{ duration:0.65, delay, ease:[0.22,1,0.36,1] }}
      className={className} style={style}>
      {children}
    </motion.div>
  )
}

/* ─── Noise overlay ────────────────────────────────────── */
function Noise() {
  return (
    <div style={{
      position:'absolute', inset:0, pointerEvents:'none', zIndex:1, opacity:0.035,
      backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")`,
      backgroundSize:'200px 200px',
    }} />
  )
}

/* ─── Dashboard Mockup ─────────────────────────────────── */
function DashboardMockup() {
  const { t } = useT()
  const services = [
    { name:'García Mendoza', type:'Velatorio – Sala A', status:'En progreso', color:'#22c55e', dot:'●' },
    { name:'López Ruiz',     type:'Cremación – 14:30', status:'Programado',  color:t.accent,  dot:'◉' },
    { name:'Hernández M.',   type:'Servicio completo', status:'Finalizado',  color:'#64748b', dot:'✓' },
  ]
  const payments = [
    { name:'García M.', amount:'$2,400', paid:true },
    { name:'López R.',  amount:'$1,800', paid:false },
    { name:'Ruiz H.',   amount:'$3,200', paid:true },
  ]
  const stats = [
    { label:'Servicios', value:'24', change:'+3', up:true,  color:t.accent },
    { label:'Ingresos',  value:'$48k', change:'+12%', up:true,  color:'#22c55e' },
    { label:'Clientes',  value:'187', change:'+8',  up:true,  color:'#8b5cf6' },
    { label:'Pendientes',value:'6',   change:'-2',  up:false, color:'#f59e0b' },
  ]

  return (
    <div style={{
      width:600, height:400, background:t.bg2, borderRadius:16,
      border:`1px solid ${t.border}`, overflow:'hidden', flexShrink:0,
      boxShadow:`0 50px 140px rgba(0,0,0,${t.isDark?'0.75':'0.18'}), 0 0 0 1px ${t.border}`,
    }}>
      {/* Topbar */}
      <div style={{ background:t.bg1, borderBottom:`1px solid ${t.border}`, padding:'9px 14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <div style={{ width:22, height:22, borderRadius:6, background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#fff', fontSize:9, fontWeight:900 }}>F</span>
          </div>
          <span style={{ color:t.textPrimary, fontSize:10, fontWeight:700 }}>FunerariaCore</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ color:t.textMuted, fontSize:8 }}>Buen día, Administrador</span>
          <div style={{ position:'relative' }}>
            <div style={{ width:18, height:18, borderRadius:5, background:t.bg3, border:`1px solid ${t.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Bell size={9} color={t.textMuted} />
            </div>
            <div style={{ position:'absolute', top:-2, right:-2, width:6, height:6, borderRadius:'50%', background:'#ef4444' }} />
          </div>
          <div style={{ width:22, height:22, borderRadius:'50%', background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#fff', fontSize:8, fontWeight:700 }}>AD</span>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', height:'calc(100% - 40px)' }}>
        {/* Sidebar */}
        <div style={{ width:96, background:t.bg1, borderRight:`1px solid ${t.border}`, padding:'10px 0', flexShrink:0 }}>
          {[
            { emoji:'▦', label:'Dashboard', active:true },
            { emoji:'👥', label:'Clientes',  active:false },
            { emoji:'⚙️', label:'Servicios', active:false },
            { emoji:'💳', label:'Pagos',     active:false },
            { emoji:'📦', label:'Inventario',active:false },
            { emoji:'📅', label:'Calendario',active:false },
            { emoji:'📊', label:'Reportes',  active:false },
          ].map(item => (
            <div key={item.label} style={{
              padding:'6px 10px', display:'flex', alignItems:'center', gap:5,
              background:item.active ? `${t.accent}18` : 'transparent',
              borderLeft:item.active ? `2px solid ${t.accent}` : '2px solid transparent',
            }}>
              <span style={{ fontSize:8 }}>{item.emoji}</span>
              <span style={{ color:item.active ? t.accent : t.textMuted, fontSize:7.5, fontWeight:item.active?600:400 }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex:1, padding:'10px 12px', overflow:'hidden' }}>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:5, marginBottom:9 }}>
            {stats.map(s => (
              <div key={s.label} style={{ background:t.bg3, borderRadius:7, padding:'7px 8px', border:`1px solid ${t.border}` }}>
                <div style={{ color:t.textMuted, fontSize:6.5, marginBottom:2 }}>{s.label}</div>
                <div style={{ color:t.textPrimary, fontSize:13, fontWeight:800, lineHeight:1 }}>{s.value}</div>
                <div style={{ display:'flex', alignItems:'center', gap:2, marginTop:3 }}>
                  {s.up ? <TrendingUp size={7} color='#22c55e'/> : <TrendingDown size={7} color='#f59e0b'/>}
                  <span style={{ color:s.up?'#22c55e':'#f59e0b', fontSize:6.5 }}>{s.change}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div style={{ background:t.bg3, borderRadius:8, padding:'8px 10px', border:`1px solid ${t.border}`, marginBottom:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
              <div>
                <span style={{ color:t.textPrimary, fontSize:8, fontWeight:600 }}>Ingresos mensuales</span>
                <span style={{ color:'#22c55e', fontSize:7, marginLeft:6, background:'rgba(34,197,94,0.12)', padding:'1px 5px', borderRadius:100 }}>↑ 12%</span>
              </div>
              <span style={{ color:t.accent, fontSize:6.5, background:`${t.accent}15`, padding:'2px 6px', borderRadius:100 }}>2024</span>
            </div>
            <svg width="100%" height="42" viewBox="0 0 280 42">
              <defs>
                <linearGradient id="cg2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={t.accent} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={t.accent} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,36 C20,32 40,22 70,18 S110,10 140,12 S190,22 220,14 L280,8 L280,42 L0,42 Z" fill="url(#cg2)" />
              <path d="M0,36 C20,32 40,22 70,18 S110,10 140,12 S190,22 220,14 L280,8" fill="none" stroke={t.accent} strokeWidth="1.5" strokeLinecap="round" />
              {[{x:0,y:36},{x:70,y:18},{x:140,y:12},{x:220,y:14},{x:280,y:8}].map((p,i)=>
                <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={t.accent} />
              )}
              {['E','F','M','A','M','J','J','A'].map((m,i)=>(
                <text key={i} x={i*38+2} y={42} fill={t.textMuted} fontSize="6" textAnchor="middle">{m}</text>
              ))}
            </svg>
          </div>

          {/* Bottom two cols */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
            {/* Services */}
            <div style={{ background:t.bg3, borderRadius:8, padding:'7px 8px', border:`1px solid ${t.border}` }}>
              <div style={{ color:t.textMuted, fontSize:7, fontWeight:600, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Servicios hoy</div>
              {services.map((s,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:5, marginBottom:5 }}>
                  <div style={{ width:20, height:20, borderRadius:'50%', background:`${t.accent}20`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <span style={{ color:t.accent, fontSize:7, fontWeight:700 }}>{s.name.charAt(0)}</span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:t.textPrimary, fontSize:7, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.name}</div>
                    <div style={{ color:t.textMuted, fontSize:6.5 }}>{s.type}</div>
                  </div>
                  <span style={{ color:s.color, fontSize:6, background:`${s.color}18`, padding:'2px 5px', borderRadius:100, whiteSpace:'nowrap' }}>{s.status}</span>
                </div>
              ))}
            </div>
            {/* Payments */}
            <div style={{ background:t.bg3, borderRadius:8, padding:'7px 8px', border:`1px solid ${t.border}` }}>
              <div style={{ color:t.textMuted, fontSize:7, fontWeight:600, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Últimos pagos</div>
              {payments.map((p,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <div style={{ width:5, height:5, borderRadius:'50%', background:p.paid?'#22c55e':'#f59e0b' }} />
                    <span style={{ color:t.textPrimary, fontSize:7 }}>{p.name}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <span style={{ color:t.textPrimary, fontSize:7.5, fontWeight:700 }}>{p.amount}</span>
                    <span style={{ color:p.paid?'#22c55e':'#f59e0b', fontSize:6, background:p.paid?'rgba(34,197,94,0.12)':'rgba(245,158,11,0.12)', padding:'1px 5px', borderRadius:100 }}>
                      {p.paid?'Pagado':'Pendiente'}
                    </span>
                  </div>
                </div>
              ))}
              <div style={{ height:1, background:t.border, margin:'5px 0' }} />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ color:t.textMuted, fontSize:6.5 }}>Total del mes</span>
                <span style={{ color:'#22c55e', fontSize:9, fontWeight:800 }}>$48,320</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── FloatingCard ─────────────────────────────────────── */
function FloatingCard({ style, delay=0, children }: { style?: React.CSSProperties; delay?: number; children: React.ReactNode }) {
  const { t } = useT()
  return (
    <motion.div
      animate={{ y:[0,-10,0] }}
      transition={{ duration:3.5+delay*0.5, repeat:Infinity, ease:'easeInOut', delay }}
      style={{
        position:'absolute',
        background:t.isDark ? 'rgba(17,20,31,0.88)' : 'rgba(255,255,255,0.92)',
        backdropFilter:'blur(24px)',
        border:`1px solid ${t.border}`,
        borderRadius:14,
        padding:'10px 14px',
        boxShadow:t.isDark ? '0 8px 40px rgba(0,0,0,0.55)' : '0 8px 32px rgba(0,0,0,0.13)',
        ...style,
      }}
    >{children}</motion.div>
  )
}

/* ─── Navbar ───────────────────────────────────────────── */
function Navbar() {
  const { t, toggle } = useT()
  const y = useScrollY()
  const [open, setOpen] = useState(false)
  const solid = y > 60
  const go = (id: string) => { setOpen(false); document.getElementById(id)?.scrollIntoView({ behavior:'smooth' }) }

  return (
    <motion.nav
      initial={{ y:-80, opacity:0 }} animate={{ y:0, opacity:1 }}
      transition={{ duration:0.6, ease:[0.22,1,0.36,1] }}
      style={{
        position:'fixed', top:0, left:0, right:0, zIndex:100,
        background: solid ? (t.isDark ? 'rgba(7,9,16,0.96)' : 'rgba(255,255,255,0.96)') : 'transparent',
        backdropFilter: solid ? 'blur(24px)' : 'none',
        borderBottom: solid ? `1px solid ${t.border}` : 'none',
        transition:'all 0.35s ease',
      }}
    >
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', height:68 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 16px ${t.shadowAccent}` }}>
            <span style={{ color:'#fff', fontWeight:900, fontSize:16 }}>F</span>
          </div>
          <span style={{ color:t.textPrimary, fontWeight:700, fontSize:18, letterSpacing:'-0.3px' }}>
            Funeraria<span style={{ color:t.accent }}>Core</span>
          </span>
        </div>

        <div className="hidden md:flex" style={{ gap:28, alignItems:'center' }}>
          {[['Inicio','hero'],['Características','features'],['Planes','pricing'],['Contacto','contact']].map(([l,id]) => (
            <button key={id} onClick={() => go(id)} style={{ color:t.textMuted, fontSize:14, fontWeight:500, background:'none', border:'none', cursor:'pointer', transition:'color 0.2s' }}
              onMouseEnter={e=>(e.currentTarget.style.color=t.textPrimary)}
              onMouseLeave={e=>(e.currentTarget.style.color=t.textMuted)}>
              {l}
            </button>
          ))}
          <Link to="/login" style={{ color:t.textMuted, fontSize:14, fontWeight:500, textDecoration:'none', transition:'color 0.2s' }}
            onMouseEnter={e=>(e.currentTarget.style.color=t.textPrimary)}
            onMouseLeave={e=>(e.currentTarget.style.color=t.textMuted)}>
            Iniciar sesión
          </Link>
          <Link to="/login" style={{ background:t.accentGrad, color:'#fff', fontWeight:600, fontSize:13, padding:'9px 20px', borderRadius:8, textDecoration:'none', boxShadow:`0 4px 16px ${t.shadowAccent}`, transition:'transform 0.2s' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)'}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none'}}>
            Solicitar Demo
          </Link>
        </div>

        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={toggle} aria-label={t.isDark ? 'Modo claro' : 'Modo oscuro'} style={{ width:36, height:36, borderRadius:8, border:`1px solid ${t.border}`, background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'border-color 0.2s' }}
            onMouseEnter={e=>(e.currentTarget.style.borderColor=`${t.accent}50`)}
            onMouseLeave={e=>(e.currentTarget.style.borderColor=t.border)}>
            {t.isDark ? <Sun size={15} color={t.textMuted}/> : <Moon size={15} color={t.textMuted}/>}
          </button>
          <button className="md:hidden" onClick={()=>setOpen(!open)} style={{ background:'none', border:'none', color:t.textPrimary, cursor:'pointer' }}>
            {open ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}
            style={{ background:t.isDark?'rgba(7,9,16,0.98)':'rgba(255,255,255,0.98)', borderBottom:`1px solid ${t.border}`, padding:'16px 24px 24px', display:'flex', flexDirection:'column', gap:16 }}>
            {[['Inicio','hero'],['Características','features'],['Planes','pricing'],['Contacto','contact']].map(([l,id]) => (
              <button key={id} onClick={()=>go(id)} style={{ color:t.textMuted, textAlign:'left', background:'none', border:'none', fontSize:15, cursor:'pointer', padding:'4px 0' }}>{l}</button>
            ))}
            <Link to="/login" style={{ color:t.textMuted, fontSize:15, textDecoration:'none' }}>Iniciar sesión</Link>
            <Link to="/login" style={{ background:t.accentGrad, color:'#fff', fontWeight:600, fontSize:14, padding:'11px 20px', borderRadius:8, textDecoration:'none', textAlign:'center' }}>Solicitar Demo</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}

/* ─── Hero ─────────────────────────────────────────────── */
function Hero() {
  const { t } = useT()
  return (
    <section id="hero" style={{ minHeight:'100vh', background:t.bg0, position:'relative', overflow:'hidden', display:'flex', alignItems:'center' }}>
      <Noise />
      {/* Orbs */}
      <motion.div animate={{ scale:[1,1.1,1], opacity:[0.6,1,0.6] }} transition={{ duration:8, repeat:Infinity }}
        style={{ position:'absolute', left:'-15%', top:'-10%', width:700, height:700, borderRadius:'50%', background:`radial-gradient(circle, ${t.accent}20 0%, transparent 70%)`, filter:'blur(80px)', pointerEvents:'none' }} />
      <motion.div animate={{ scale:[1,1.15,1], opacity:[0.5,0.8,0.5] }} transition={{ duration:10, repeat:Infinity, delay:2 }}
        style={{ position:'absolute', right:'-10%', bottom:'-10%', width:600, height:600, borderRadius:'50%', background:`radial-gradient(circle, #8b5cf620 0%, transparent 70%)`, filter:'blur(80px)', pointerEvents:'none' }} />
      <motion.div animate={{ scale:[1,1.08,1] }} transition={{ duration:7, repeat:Infinity, delay:1 }}
        style={{ position:'absolute', right:'25%', top:'15%', width:300, height:300, borderRadius:'50%', background:`radial-gradient(circle, #06b6d415 0%, transparent 70%)`, filter:'blur(60px)', pointerEvents:'none' }} />
      {/* Grid */}
      <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(${t.isDark?'rgba(255,255,255,0.028)':'rgba(0,0,0,0.028)'} 1px, transparent 1px), linear-gradient(90deg,${t.isDark?'rgba(255,255,255,0.028)':'rgba(0,0,0,0.028)'} 1px, transparent 1px)`, backgroundSize:'60px 60px', pointerEvents:'none' }} />

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'120px 24px 80px', width:'100%', position:'relative', zIndex:2 }}>
        <div className="grid md:grid-cols-2 items-center" style={{ gap:52 }}>

          {/* Left */}
          <div>
            <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.1 }}
              style={{ display:'inline-flex', alignItems:'center', gap:8, background:`${t.accent}15`, border:`1px solid ${t.accent}30`, borderRadius:100, padding:'6px 16px', marginBottom:24 }}>
              <motion.div animate={{ scale:[1,1.3,1] }} transition={{ duration:2, repeat:Infinity }}
                style={{ width:7, height:7, borderRadius:'50%', background:t.accent }} />
              <span style={{ color:t.accent, fontSize:12, fontWeight:600 }}>La nueva generación de software funerario</span>
            </motion.div>

            <motion.h1 initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7, delay:0.2 }}
              style={{ color:t.textPrimary, fontSize:'clamp(32px,4.5vw,54px)', fontWeight:800, lineHeight:1.08, letterSpacing:'-1.5px', marginBottom:20 }}>
              Gestiona tu funeraria con{' '}
              <span style={{ background:`linear-gradient(135deg, ${t.accentLight}, ${t.accent}, #8b5cf6)`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                velocidad, control y elegancia
              </span>
            </motion.h1>

            <motion.p initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.35 }}
              style={{ color:t.textMuted, fontSize:17, lineHeight:1.75, marginBottom:36, maxWidth:460 }}>
              Administra servicios, clientes, pagos, contratos e inventario en tiempo real. Todo en una plataforma diseñada para funerarias de alto nivel.
            </motion.p>

            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.5 }}
              style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <Link to="/login" style={{ background:t.accentGrad, color:'#fff', fontWeight:700, fontSize:15, padding:'14px 28px', borderRadius:10, textDecoration:'none', display:'flex', alignItems:'center', gap:8, boxShadow:`0 8px 32px ${t.shadowAccent}`, transition:'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 16px 40px ${t.shadowAccent}` }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 8px 32px ${t.shadowAccent}` }}>
                Solicitar Demo <ArrowRight size={16}/>
              </Link>
              <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior:'smooth' })}
                style={{ background:'transparent', color:t.textPrimary, fontWeight:600, fontSize:15, padding:'14px 28px', borderRadius:10, border:`1px solid ${t.border}`, cursor:'pointer', transition:'all 0.2s' }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor=`${t.accent}60`; e.currentTarget.style.background=`${t.accent}0a` }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor=t.border; e.currentTarget.style.background='transparent' }}>
                Ver Funciones
              </button>
            </motion.div>

            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:0.6, delay:0.7 }}
              style={{ display:'flex', alignItems:'center', gap:16, marginTop:32 }}>
              <div style={{ display:'flex' }}>
                {['CM','MG','RV','JL'].map((initials,i) => (
                  <div key={i} style={{ width:30, height:30, borderRadius:'50%', border:`2px solid ${t.bg0}`, marginLeft:i?-8:0, background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ color:'#fff', fontSize:7, fontWeight:700 }}>{initials}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ display:'flex', gap:2 }}>
                  {[0,1,2,3,4].map(i => <Star key={i} size={12} fill={t.accent} color={t.accent}/>)}
                </div>
                <span style={{ color:t.textMuted, fontSize:12 }}>+200 funerarias confían en nosotros</span>
              </div>
            </motion.div>
          </div>

          {/* Right — mockup + floating cards */}
          <motion.div initial={{ opacity:0, x:40 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.85, delay:0.3 }}
            style={{ position:'relative', display:'flex', justifyContent:'center' }}>
            {/* Big glow behind dashboard */}
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:520, height:520, borderRadius:'50%', background:`radial-gradient(circle, ${t.accent}22 0%, transparent 68%)`, filter:'blur(60px)', pointerEvents:'none' }} />
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:300, height:300, borderRadius:'50%', background:`radial-gradient(circle, #8b5cf625 0%, transparent 65%)`, filter:'blur(40px)', pointerEvents:'none' }} />

            <motion.div animate={{ y:[0,-12,0] }} transition={{ duration:4.5, repeat:Infinity, ease:'easeInOut' }}>
              <DashboardMockup />
            </motion.div>

            {/* Floating cards */}
            <FloatingCard style={{ top:-24, right:-8, minWidth:160 }} delay={0}>
              <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                <div style={{ width:30, height:30, borderRadius:9, background:`${t.accent}20`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <TrendingUp size={14} color={t.accent}/>
                </div>
                <div>
                  <div style={{ color:t.textMuted, fontSize:9 }}>Ingresos este mes</div>
                  <div style={{ color:t.textPrimary, fontSize:15, fontWeight:800 }}>$48,320</div>
                </div>
              </div>
              <div style={{ color:'#22c55e', fontSize:9, marginTop:5 }}>↑ 12% vs mes anterior</div>
            </FloatingCard>

            <FloatingCard style={{ bottom:20, left:-24, minWidth:148 }} delay={0.4}>
              <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#22c55e' }} />
                <span style={{ color:t.textMuted, fontSize:9 }}>Servicios activos hoy</span>
              </div>
              <div style={{ color:t.textPrimary, fontSize:20, fontWeight:800, margin:'4px 0' }}>24</div>
              <div style={{ display:'flex', gap:3, alignItems:'flex-end' }}>
                {[0.5,0.7,0.4,1,0.6,0.9,0.8].map((h,i)=>(
                  <div key={i} style={{ width:7, height:18*h, borderRadius:3, background:`${t.accent}${Math.round(h*200+55).toString(16)}`, alignSelf:'flex-end' }} />
                ))}
              </div>
            </FloatingCard>

            <FloatingCard style={{ top:'42%', right:-32, minWidth:140 }} delay={0.7}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
                <CheckCircle2 size={13} color='#22c55e'/>
                <span style={{ color:'#22c55e', fontSize:9, fontWeight:600 }}>Contrato firmado</span>
              </div>
              <div style={{ color:t.textPrimary, fontSize:10, fontWeight:600 }}>García Mendoza</div>
              <div style={{ color:t.textMuted, fontSize:8, marginTop:2 }}>Velatorio Sala A · hace 3 min</div>
            </FloatingCard>

            <FloatingCard style={{ top:60, left:-16, minWidth:136 }} delay={1.1}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
                <DollarSign size={12} color={t.accent}/>
                <span style={{ color:t.textMuted, fontSize:9 }}>Pago recibido</span>
              </div>
              <div style={{ color:t.textPrimary, fontSize:13, fontWeight:800 }}>$2,400</div>
              <div style={{ color:t.textMuted, fontSize:8, marginTop:2 }}>López Ruiz · Contado</div>
            </FloatingCard>
          </motion.div>
        </div>
      </div>

      <motion.div animate={{ y:[0,8,0] }} transition={{ duration:2, repeat:Infinity }}
        style={{ position:'absolute', bottom:32, left:'50%', transform:'translateX(-50%)', cursor:'pointer', zIndex:2 }}
        onClick={() => document.getElementById('social-proof')?.scrollIntoView({ behavior:'smooth' })}>
        <ChevronDown size={22} color={t.textMuted}/>
      </motion.div>
    </section>
  )
}

/* ─── Social Proof ─────────────────────────────────────── */
function SocialProof() {
  const { t } = useT()
  const stats = [
    { value:200,  prefix:'+', suffix:'',  label:'Funerarias activas' },
    { value:12000,prefix:'+', suffix:'',  label:'Servicios registrados' },
    { value:99.9, prefix:'',  suffix:'%', label:'Uptime garantizado', dur:1.5 },
    { value:70,   prefix:'-', suffix:'%', label:'Tiempo administrativo' },
    { value:24,   prefix:'',  suffix:'/7',label:'Soporte dedicado' },
  ]
  return (
    <section id="social-proof" style={{ background:t.bg1, borderTop:`1px solid ${t.border}`, borderBottom:`1px solid ${t.border}`, padding:'36px 24px', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(ellipse 50% 100% at 50% 50%, ${t.glowBg} 0%, transparent 70%)`, pointerEvents:'none' }} />
      <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', justifyContent:'space-around', flexWrap:'wrap', gap:24, position:'relative', zIndex:1 }}>
        {stats.map((s,i) => (
          <AnimatedSection key={i} delay={i*0.08} style={{ textAlign:'center' }}>
            <div style={{ color:t.textPrimary, fontSize:'clamp(26px,3.5vw,38px)', fontWeight:900, letterSpacing:'-1px', lineHeight:1 }}>
              <Counter end={s.value} prefix={s.prefix} suffix={s.suffix} dur={s.dur ?? 2}/>
            </div>
            <div style={{ color:t.textMuted, fontSize:13, marginTop:6 }}>{s.label}</div>
          </AnimatedSection>
        ))}
      </div>
    </section>
  )
}

/* ─── Benefits ─────────────────────────────────────────── */
function Benefits() {
  const { t } = useT()
  const items = [
    { icon:Layers,    title:'Gestión Completa',           desc:'Servicios, clientes, salas, ataúdes, documentos y facturación desde un solo panel unificado.' },
    { icon:Building2, title:'Multi Sucursal',              desc:'Administra múltiples funerarias y sucursales desde un panel centralizado, sin complicaciones.' },
    { icon:Globe,     title:'Acceso desde cualquier lugar',desc:'Compatible con móvil, tablet y computadora. Tu negocio siempre a la mano, en cualquier momento.' },
    { icon:Lock,      title:'Seguridad y respaldos',       desc:'Información cifrada con AES-256, protegida y respaldada automáticamente en la nube cada hora.' },
  ]
  return (
    <section style={{ background:t.bg0, padding:'88px 24px', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:`linear-gradient(90deg, transparent, ${t.accent}30, transparent)` }} />
      <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:56 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>¿Por qué FunerariaCore?</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>Todo lo que necesitas en un solo lugar</h2>
        </AnimatedSection>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap:18 }}>
          {items.map((item,i) => (
            <AnimatedSection key={i} delay={i*0.1}>
              <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:16, padding:'26px 22px', transition:'all 0.3s', cursor:'default', height:'100%' }}
                onMouseEnter={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=`${t.accent}40`; d.style.transform='translateY(-5px)'; d.style.boxShadow=`0 20px 50px ${t.shadowAccent}20` }}
                onMouseLeave={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=t.border; d.style.transform='none'; d.style.boxShadow='none' }}>
                <div style={{ width:48, height:48, borderRadius:13, background:`${t.accent}18`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                  <item.icon size={22} color={t.accent}/>
                </div>
                <h3 style={{ color:t.textPrimary, fontWeight:700, fontSize:15.5, marginBottom:8 }}>{item.title}</h3>
                <p style={{ color:t.textMuted, fontSize:13.5, lineHeight:1.65 }}>{item.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Problem → Solution ───────────────────────────────── */
function ProblemSolution() {
  const { t } = useT()
  const probs = [
    'Excel y papeles dispersos sin control',
    'Contratos en físico que se pierden',
    'Sin control financiero claro',
    'Comunicación caótica por WhatsApp',
    'Reportes manuales lentos e inexactos',
    'Sin acceso fuera de la oficina',
  ]
  const sols = [
    'Todo centralizado en la nube, 24/7',
    'Contratos digitales firmados al instante',
    'Dashboard financiero en tiempo real',
    'Sistema integrado de comunicación',
    'Reportes automáticos con un clic',
    'Acceso desde cualquier dispositivo',
  ]
  return (
    <section style={{ background:t.bg1, padding:'88px 24px', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(ellipse 60% 50% at 50% 50%, ${t.glowBg} 0%, transparent 70%)`, pointerEvents:'none' }} />
      <div style={{ maxWidth:1100, margin:'0 auto', position:'relative', zIndex:1 }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:56 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>El cambio que necesitas</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>
            La administración funeraria<br />
            <span style={{ background:`linear-gradient(135deg, ${t.accentLight}, ${t.accent})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              no tiene que ser caótica
            </span>
          </h2>
        </AnimatedSection>

        <div className="grid md:grid-cols-2" style={{ gap:20 }}>
          {/* Before */}
          <AnimatedSection>
            <div style={{ background:t.bg2, border:`1px solid rgba(239,68,68,0.18)`, borderRadius:18, padding:'28px 28px', height:'100%' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <XCircle size={18} color='#ef4444'/>
                </div>
                <div>
                  <div style={{ color:t.textPrimary, fontWeight:700, fontSize:16 }}>Sin FunerariaCore</div>
                  <div style={{ color:'#ef4444', fontSize:12 }}>El día a día de la mayoría</div>
                </div>
              </div>
              {probs.map((p,i) => (
                <motion.div key={i} initial={{ opacity:0, x:-16 }} whileInView={{ opacity:1, x:0 }} transition={{ delay:i*0.08, duration:0.4 }} viewport={{ once:true }}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.1)', borderRadius:10, marginBottom:10 }}>
                  <X size={14} color='#ef4444' style={{ flexShrink:0 }}/>
                  <span style={{ color:t.textMuted, fontSize:14 }}>{p}</span>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

          {/* After */}
          <AnimatedSection delay={0.15}>
            <div style={{ background:t.bg2, border:`1px solid ${t.accent}30`, borderRadius:18, padding:'28px 28px', height:'100%', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, right:0, width:200, height:200, borderRadius:'50%', background:`${t.accent}10`, filter:'blur(50px)', pointerEvents:'none' }} />
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:`${t.accent}18`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <CheckCircle2 size={18} color={t.accent}/>
                </div>
                <div>
                  <div style={{ color:t.textPrimary, fontWeight:700, fontSize:16 }}>Con FunerariaCore</div>
                  <div style={{ color:t.accent, fontSize:12 }}>Control total desde el día uno</div>
                </div>
              </div>
              {sols.map((s,i) => (
                <motion.div key={i} initial={{ opacity:0, x:16 }} whileInView={{ opacity:1, x:0 }} transition={{ delay:i*0.08, duration:0.4 }} viewport={{ once:true }}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', background:`${t.accent}08`, border:`1px solid ${t.accent}18`, borderRadius:10, marginBottom:10 }}>
                  <Check size={14} color={t.accent} style={{ flexShrink:0 }}/>
                  <span style={{ color:t.textPrimary, fontSize:14 }}>{s}</span>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}

/* ─── Workflow ─────────────────────────────────────────── */
function Workflow() {
  const { t } = useT()
  const steps = [
    { icon:ClipboardList, n:'01', title:'Registrar servicio', desc:'Ingresa los datos del difunto, familia y tipo de servicio en segundos.' },
    { icon:Users,         n:'02', title:'Asignar recursos',   desc:'Asigna sala, personal, ataúd y materiales desde el inventario.' },
    { icon:FileText,      n:'03', title:'Generar contrato',   desc:'Contrato digital listo para firma en un solo clic.' },
    { icon:CreditCard,    n:'04', title:'Gestionar pagos',    desc:'Registra pagos parciales, cuotas y genera recibos automáticos.' },
    { icon:Activity,      n:'05', title:'Seguimiento',        desc:'Monitorea el estado del servicio en tiempo real desde cualquier device.' },
    { icon:BarChart3,     n:'06', title:'Reportes',           desc:'Cierra el ciclo con reportes financieros y administrativos automáticos.' },
  ]
  return (
    <section style={{ background:t.bg0, padding:'88px 24px', position:'relative', overflow:'hidden' }}>
      <Noise />
      <div style={{ maxWidth:1200, margin:'0 auto', position:'relative', zIndex:1 }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:64 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>Flujo de trabajo</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>Del registro al reporte en 6 pasos</h2>
          <p style={{ color:t.textMuted, fontSize:16, marginTop:12, maxWidth:480, margin:'12px auto 0' }}>Un proceso claro y estructurado para cada servicio funerario.</p>
        </AnimatedSection>

        {/* Steps grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3" style={{ gap:18 }}>
          {steps.map((s,i) => (
            <AnimatedSection key={i} delay={i*0.1}>
              <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:16, padding:'24px', transition:'all 0.3s', position:'relative', overflow:'hidden' }}
                onMouseEnter={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=`${t.accent}40`; d.style.transform='translateY(-4px)'; d.style.boxShadow=`0 20px 40px ${t.shadowAccent}18` }}
                onMouseLeave={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=t.border; d.style.transform='none'; d.style.boxShadow='none' }}>
                <div style={{ position:'absolute', top:16, right:16, color:t.accent, fontSize:28, fontWeight:900, opacity:0.1, lineHeight:1 }}>{s.n}</div>
                <div style={{ width:44, height:44, borderRadius:12, background:`${t.accent}18`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                  <s.icon size={20} color={t.accent}/>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <span style={{ color:t.accent, fontSize:11, fontWeight:700 }}>Paso {s.n}</span>
                  {i < steps.length-1 && <ChevronRight size={12} color={t.textMuted}/>}
                </div>
                <h3 style={{ color:t.textPrimary, fontWeight:700, fontSize:16, marginBottom:8 }}>{s.title}</h3>
                <p style={{ color:t.textMuted, fontSize:13.5, lineHeight:1.65 }}>{s.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Features ─────────────────────────────────────────── */
function Features() {
  const { t } = useT()
  const modules = [
    { icon:ClipboardList, title:'Gestión de Servicios Funerarios',
      desc:'Registra y controla cada servicio desde la recepción hasta la entrega final. Asigna salas, personal y recursos automáticamente.',
      points:['Seguimiento en tiempo real','Asignación de recursos automática','Notificaciones al cliente y familia'] },
    { icon:Users, title:'Clientes y Familiares',
      desc:'Historial completo de cada cliente. Gestión de contactos, preferencias, historial de servicios y documentos en un perfil unificado.',
      points:['Historial completo de servicios','Comunicación integrada','Documentos por cliente'] },
    { icon:CreditCard, title:'Facturación y Pagos',
      desc:'Genera facturas profesionales, registra pagos parciales, controla cuotas y lleva el control financiero completo de tu funeraria.',
      points:['Facturas profesionales PDF','Registro de cuotas y abonos','Estado financiero en tiempo real'] },
    { icon:FileText, title:'Contratos y Documentos PDF',
      desc:'Genera contratos digitales listos para firma y exporta cualquier documento en PDF con el branding de tu funeraria al instante.',
      points:['Contratos con firma digital','Exportación PDF instantánea','Plantillas completamente personalizables'] },
  ]
  return (
    <section id="features" style={{ background:t.bg1, padding:'88px 24px' }}>
      <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:64 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>Módulos del sistema</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>Una plataforma. Todas las funciones.</h2>
        </AnimatedSection>
        <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
          {modules.map((mod,i) => (
            <AnimatedSection key={i} delay={0.05}>
              <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:18, padding:'28px 32px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:36, alignItems:'center', transition:'border-color 0.3s' }}
                onMouseEnter={e=>(e.currentTarget.style.borderColor=`${t.accent}35`)}
                onMouseLeave={e=>(e.currentTarget.style.borderColor=t.border)}>
                <div style={{ order:i%2===0?0:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:`${t.accent}18`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <mod.icon size={20} color={t.accent}/>
                    </div>
                    <h3 style={{ color:t.textPrimary, fontWeight:700, fontSize:18 }}>{mod.title}</h3>
                  </div>
                  <p style={{ color:t.textMuted, fontSize:15, lineHeight:1.7, marginBottom:16 }}>{mod.desc}</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
                    {mod.points.map((p,j) => (
                      <div key={j} style={{ display:'flex', alignItems:'center', gap:9 }}>
                        <div style={{ width:18, height:18, borderRadius:'50%', background:`${t.accent}18`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <Check size={10} color={t.accent}/>
                        </div>
                        <span style={{ color:t.textMuted, fontSize:13.5 }}>{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ order:i%2===0?1:0, display:'flex', justifyContent:'center' }}>
                  <div style={{ width:'100%', maxWidth:340, height:190, background:t.bg3, borderRadius:14, border:`1px solid ${t.border}`, overflow:'hidden', position:'relative', boxShadow:`0 20px 60px rgba(0,0,0,${t.isDark?'0.4':'0.1'})` }}>
                    <div style={{ background:t.bg1, height:34, borderBottom:`1px solid ${t.border}`, display:'flex', alignItems:'center', padding:'0 12px', gap:6 }}>
                      {[0,1,2].map(j => <div key={j} style={{ width:8, height:8, borderRadius:'50%', background:['#ef4444','#f59e0b','#22c55e'][j] }}/>)}
                      <span style={{ color:t.textMuted, fontSize:9, marginLeft:6, flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{mod.title}</span>
                    </div>
                    <div style={{ padding:14 }}>
                      {[100,55,82,38,70].map((w,j)=>(
                        <div key={j} style={{ height:8, borderRadius:4, background:j%3===0?`${t.accent}28`:t.border, width:`${w}%`, marginBottom:9 }}/>
                      ))}
                      <div style={{ display:'flex', gap:7, marginTop:14 }}>
                        <div style={{ height:26, flex:1, borderRadius:7, background:t.accentGrad, opacity:0.7 }}/>
                        <div style={{ height:26, flex:2, borderRadius:7, background:t.bg2, border:`1px solid ${t.border}` }}/>
                      </div>
                    </div>
                    <div style={{ position:'absolute', inset:0, background:`linear-gradient(135deg, transparent 55%, ${t.accent}08)` }}/>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Dashboard showcase ───────────────────────────────── */
function DashboardShowcase() {
  const { t } = useT()
  const metrics = [
    { label:'Ingresos mensuales', value:'$48,320', change:'+12%', color:t.accent },
    { label:'Servicios activos',  value:'24',      change:'+3 esta semana', color:'#22c55e' },
    { label:'Pagos pendientes',   value:'$8,640',  change:'6 facturas', color:'#f59e0b' },
    { label:'Clientes totales',   value:'187',     change:'+8 este mes', color:'#8b5cf6' },
  ]
  return (
    <section style={{ background:t.bg0, padding:'88px 24px', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(ellipse 60% 50% at 50% 50%, ${t.glowBg} 0%, transparent 70%)`, pointerEvents:'none' }}/>
      <Noise />
      <div style={{ maxWidth:1200, margin:'0 auto', position:'relative', zIndex:1 }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:56 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>Dashboard ejecutivo</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>Toma decisiones con información en tiempo real</h2>
        </AnimatedSection>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap:16, marginBottom:40 }}>
          {metrics.map((m,i) => (
            <AnimatedSection key={i} delay={i*0.08}>
              <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:14, padding:'22px 20px', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, right:0, width:80, height:80, borderRadius:'50%', background:`${m.color}12`, filter:'blur(24px)' }}/>
                <div style={{ color:t.textMuted, fontSize:12, marginBottom:8 }}>{m.label}</div>
                <div style={{ color:t.textPrimary, fontSize:26, fontWeight:800, letterSpacing:'-0.5px' }}>{m.value}</div>
                <div style={{ color:m.color, fontSize:12, marginTop:6 }}>{m.change}</div>
              </div>
            </AnimatedSection>
          ))}
        </div>
        <AnimatedSection>
          <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:20, overflow:'hidden', boxShadow:`0 40px 100px rgba(0,0,0,${t.isDark?'0.5':'0.1'})` }}>
            <div style={{ background:t.bg1, padding:'12px 20px', borderBottom:`1px solid ${t.border}`, display:'flex', alignItems:'center', gap:8 }}>
              {[0,1,2].map(i=><div key={i} style={{ width:10, height:10, borderRadius:'50%', background:['#ef4444','#f59e0b','#22c55e'][i] }}/>)}
              <span style={{ color:t.textMuted, fontSize:12, marginLeft:8 }}>FunerariaCore — Dashboard ejecutivo</span>
            </div>
            <div style={{ padding:24 }}>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
                <div style={{ background:t.bg3, borderRadius:14, padding:20 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <span style={{ color:t.textPrimary, fontWeight:600, fontSize:14 }}>Ingresos por mes</span>
                    <span style={{ color:t.accent, fontSize:11, background:`${t.accent}15`, padding:'4px 10px', borderRadius:100 }}>2024</span>
                  </div>
                  <svg width="100%" height="120" viewBox="0 0 400 120">
                    <defs>
                      <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={t.accent} stopOpacity="0.35"/>
                        <stop offset="100%" stopColor={t.accent} stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                    <path d="M0,90 C30,80 60,60 90,55 S140,30 180,35 S230,50 260,30 S320,10 360,20 L400,15 L400,120 L0,120 Z" fill="url(#g3)"/>
                    <path d="M0,90 C30,80 60,60 90,55 S140,30 180,35 S230,50 260,30 S320,10 360,20 L400,15" fill="none" stroke={t.accent} strokeWidth="2"/>
                    {[{x:0,y:90},{x:90,y:55},{x:180,y:35},{x:260,y:30},{x:360,y:20},{x:400,y:15}].map((p,i)=>
                      <circle key={i} cx={p.x} cy={p.y} r="4" fill={t.accent}/>
                    )}
                    {['Ene','Feb','Mar','Abr','May','Jun'].map((m,i)=>(
                      <text key={i} x={i*80} y={115} fill={t.textMuted} fontSize="9" textAnchor="middle">{m}</text>
                    ))}
                  </svg>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  <div style={{ background:t.bg3, borderRadius:14, padding:16, flex:1 }}>
                    <div style={{ color:t.textMuted, fontSize:11, marginBottom:10 }}>Por tipo de servicio</div>
                    <svg width="90" height="90" viewBox="0 0 100 100" style={{ display:'block', margin:'0 auto' }}>
                      <circle cx="50" cy="50" r="35" fill="none" stroke={t.bg2} strokeWidth="14"/>
                      <circle cx="50" cy="50" r="35" fill="none" stroke={t.accent} strokeWidth="14" strokeDasharray="110 110" strokeDashoffset="27" strokeLinecap="round" transform="rotate(-90 50 50)"/>
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#8b5cf6" strokeWidth="14" strokeDasharray="55 165" strokeDashoffset="-83" strokeLinecap="round" transform="rotate(-90 50 50)"/>
                      <text x="50" y="54" fill={t.textPrimary} fontSize="13" fontWeight="800" textAnchor="middle">24</text>
                    </svg>
                    <div style={{ display:'flex', gap:10, justifyContent:'center', marginTop:10 }}>
                      {[{c:t.accent,l:'Velatorio'},{c:'#8b5cf6',l:'Cremación'}].map((d,i)=>(
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <div style={{ width:7, height:7, borderRadius:2, background:d.c }}/>
                          <span style={{ color:t.textMuted, fontSize:9 }}>{d.l}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background:t.bg3, borderRadius:14, padding:14 }}>
                    <div style={{ color:t.textMuted, fontSize:11, marginBottom:10 }}>Actividad reciente</div>
                    {[{text:'Pago $2,400 recibido',c:t.accent},{text:'Contrato #1042 firmado',c:'#22c55e'},{text:'Servicio completado',c:'#8b5cf6'}].map((a,i)=>(
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:7 }}>
                        <div style={{ width:5, height:5, borderRadius:'50%', background:a.c }}/>
                        <span style={{ color:t.textMuted, fontSize:9 }}>{a.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  )
}

/* ─── Pricing ──────────────────────────────────────────── */
function Pricing() {
  const { t } = useT()
  return <LandingPricing theme={t} />
}

/* ─── Testimonials ─────────────────────────────────────── */
function Testimonials() {
  const { t } = useT()
  const items = [
    { name:'Carlos Méndez', role:'Director — Funeraria San Miguel', avatar:'CM',
      text:'FunerariaCore transformó completamente nuestra operación. Reducimos el tiempo administrativo en un 70% y tenemos control total de cada servicio desde el celular.' },
    { name:'María González', role:'Administradora — Grupo Funerario del Norte', avatar:'MG',
      text:'La gestión de pagos y contratos ahora es automática. Antes tardábamos horas en generar documentos, hoy lo hacemos en minutos. Una herramienta indispensable.' },
    { name:'Roberto Vargas', role:'Propietario — Funeraria Vargas e Hijos', avatar:'RV',
      text:'Como dueño de tres sucursales, poder ver todo desde un panel es invaluable. El soporte es excelente y la plataforma es muy fácil de aprender.' },
  ]
  return (
    <section style={{ background:t.bg0, padding:'88px 24px' }}>
      <div style={{ maxWidth:1100, margin:'0 auto' }}>
        <AnimatedSection style={{ textAlign:'center', marginBottom:56 }}>
          <span style={{ color:t.accent, fontSize:12, fontWeight:700, letterSpacing:2, textTransform:'uppercase' }}>Testimonios</span>
          <h2 style={{ color:t.textPrimary, fontSize:'clamp(26px,3.8vw,40px)', fontWeight:800, marginTop:12, letterSpacing:'-0.5px' }}>Funerarias que ya dieron el salto</h2>
        </AnimatedSection>
        <div className="grid md:grid-cols-3" style={{ gap:18 }}>
          {items.map((item,i) => (
            <AnimatedSection key={i} delay={i*0.12}>
              <div style={{ background:t.bg2, border:`1px solid ${t.border}`, borderRadius:18, padding:28, height:'100%', transition:'all 0.3s' }}
                onMouseEnter={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=`${t.accent}35`; d.style.transform='translateY(-5px)'; d.style.boxShadow=`0 24px 48px ${t.shadowAccent}18` }}
                onMouseLeave={e=>{ const d=e.currentTarget as HTMLDivElement; d.style.borderColor=t.border; d.style.transform='none'; d.style.boxShadow='none' }}>
                <div style={{ display:'flex', gap:3, marginBottom:16 }}>
                  {[0,1,2,3,4].map(j=><Star key={j} size={14} fill={t.accent} color={t.accent}/>)}
                </div>
                <p style={{ color:t.textMuted, fontSize:14, lineHeight:1.8, marginBottom:24, fontStyle:'italic' }}>"{item.text}"</p>
                <div style={{ display:'flex', alignItems:'center', gap:12, borderTop:`1px solid ${t.border}`, paddingTop:16 }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ color:'#fff', fontWeight:700, fontSize:13 }}>{item.avatar}</span>
                  </div>
                  <div>
                    <div style={{ color:t.textPrimary, fontWeight:600, fontSize:14 }}>{item.name}</div>
                    <div style={{ color:t.textMuted, fontSize:12 }}>{item.role}</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── CTA ──────────────────────────────────────────────── */
function CTA() {
  const { t } = useT()
  return (
    <section style={{ background:t.bg1, padding:'96px 24px', position:'relative', overflow:'hidden' }}>
      <Noise />
      <motion.div animate={{ scale:[1,1.15,1], opacity:[0.5,0.9,0.5] }} transition={{ duration:9, repeat:Infinity }}
        style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:700, height:700, borderRadius:'50%', background:`radial-gradient(circle, ${t.accent}14 0%, transparent 70%)`, filter:'blur(80px)', pointerEvents:'none' }}/>
      <motion.div animate={{ scale:[1,1.1,1] }} transition={{ duration:7, repeat:Infinity, delay:2 }}
        style={{ position:'absolute', top:'40%', right:'-10%', width:400, height:400, borderRadius:'50%', background:`radial-gradient(circle, #8b5cf618 0%, transparent 70%)`, filter:'blur(60px)', pointerEvents:'none' }}/>

      <AnimatedSection style={{ textAlign:'center', position:'relative', maxWidth:640, margin:'0 auto', zIndex:1 }}>
        <div style={{ width:64, height:64, borderRadius:18, background:`${t.accent}20`, border:`1px solid ${t.accent}30`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
          <Zap size={28} color={t.accent}/>
        </div>
        <h2 style={{ color:t.textPrimary, fontSize:'clamp(30px,4.5vw,50px)', fontWeight:800, letterSpacing:'-1px', marginBottom:16 }}>
          Lleva tu funeraria<br />
          <span style={{ background:`linear-gradient(135deg, ${t.accentLight}, ${t.accent}, #8b5cf6)`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            al siguiente nivel
          </span>
        </h2>
        <p style={{ color:t.textMuted, fontSize:17, lineHeight:1.75, marginBottom:36 }}>
          Únete a más de 200 funerarias que ya modernizaron su gestión. Solicita una demostración gratuita, sin compromisos.
        </p>
        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          <Link to="/login" style={{ background:t.accentGrad, color:'#fff', fontWeight:700, fontSize:16, padding:'16px 36px', borderRadius:12, textDecoration:'none', display:'inline-flex', alignItems:'center', gap:8, boxShadow:`0 8px 36px ${t.shadowAccent}`, transition:'transform 0.2s, box-shadow 0.2s' }}
            onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 18px 48px ${t.shadowAccent}` }}
            onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 8px 36px ${t.shadowAccent}` }}>
            Solicitar una demostración <ArrowRight size={18}/>
          </Link>
          <Link to="/login" style={{ background:'transparent', color:t.textPrimary, fontWeight:600, fontSize:16, padding:'16px 36px', borderRadius:12, border:`1px solid ${t.border}`, textDecoration:'none', transition:'border-color 0.2s' }}
            onMouseEnter={e=>(e.currentTarget.style.borderColor=`${t.accent}50`)}
            onMouseLeave={e=>(e.currentTarget.style.borderColor=t.border)}>
            Iniciar sesión
          </Link>
        </div>
      </AnimatedSection>
    </section>
  )
}

/* ─── Footer ───────────────────────────────────────────── */
function Footer() {
  const { t } = useT()
  return (
    <footer id="contact" style={{ background:t.bg0, borderTop:`1px solid ${t.border}`, padding:'64px 24px 32px' }}>
      <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap:40, marginBottom:48 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:t.accentGrad, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:'#fff', fontWeight:900, fontSize:16 }}>F</span>
              </div>
              <span style={{ color:t.textPrimary, fontWeight:700, fontSize:18 }}>Funeraria<span style={{ color:t.accent }}>Core</span></span>
            </div>
            <p style={{ color:t.textMuted, fontSize:13, lineHeight:1.7, marginBottom:20 }}>La plataforma SaaS líder en gestión funeraria. Moderniza tu negocio hoy.</p>
            <div style={{ display:'flex', gap:10 }}>
              {[Facebook,Twitter,Linkedin,Instagram].map((Icon,i)=>(
                <div key={i} style={{ width:36, height:36, borderRadius:8, background:t.bg2, border:`1px solid ${t.border}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'border-color 0.2s' }}
                  onMouseEnter={e=>(e.currentTarget.style.borderColor=`${t.accent}40`)}
                  onMouseLeave={e=>(e.currentTarget.style.borderColor=t.border)}>
                  <Icon size={15} color={t.textMuted}/>
                </div>
              ))}
            </div>
          </div>
          {[
            { title:'Producto', links:['Características','Planes','Integraciones','Seguridad','Roadmap'] },
            { title:'Empresa',  links:['Sobre nosotros','Blog','Casos de éxito','Afiliados','Empleo'] },
          ].map((col)=>(
            <div key={col.title}>
              <div style={{ color:t.textPrimary, fontWeight:600, fontSize:13, marginBottom:16 }}>{col.title}</div>
              {col.links.map((l)=>(
                <div key={l} style={{ color:t.textMuted, fontSize:13, marginBottom:10, cursor:'pointer', transition:'color 0.2s' }}
                  onMouseEnter={e=>(e.currentTarget.style.color=t.textPrimary)}
                  onMouseLeave={e=>(e.currentTarget.style.color=t.textMuted)}>{l}</div>
              ))}
            </div>
          ))}
          <div>
            <div style={{ color:t.textPrimary, fontWeight:600, fontSize:13, marginBottom:16 }}>Contacto</div>
            {[
              { icon:Mail, text:'soporte@funerariacore.com' },
              { icon:Phone, text:'+1 (800) 123-4567' },
              { icon:MapPin, text:'Ciudad de México, México' },
            ].map((c,i)=>(
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:12 }}>
                <c.icon size={14} color={t.accent} style={{ marginTop:1 }}/>
                <span style={{ color:t.textMuted, fontSize:13 }}>{c.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ borderTop:`1px solid ${t.border}`, paddingTop:24, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <span style={{ color:t.textMuted, fontSize:12 }}>© 2024 FunerariaCore. Todos los derechos reservados.</span>
          <div style={{ display:'flex', gap:20 }}>
            {['Privacidad','Términos','Cookies'].map((l)=>(
              <span key={l} style={{ color:t.textMuted, fontSize:12, cursor:'pointer', transition:'color 0.2s' }}
                onMouseEnter={e=>(e.currentTarget.style.color=t.textPrimary)}
                onMouseLeave={e=>(e.currentTarget.style.color=t.textMuted)}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ─── Root ─────────────────────────────────────────────── */
export default function LandingPage() {
  const [isDark, setIsDark] = useState(true)
  const t = isDark ? dark : light
  return (
    <Ctx.Provider value={{ t, toggle: () => setIsDark(d => !d) }}>
      <div style={{ fontFamily:'Inter, system-ui, sans-serif', background:t.bg0, color:t.textPrimary, transition:'background 0.3s, color 0.3s' }}>
        <Navbar />
        <Hero />
        <SocialProof />
        <Benefits />
        <ProblemSolution />
        <Workflow />
        <Features />
        <DashboardShowcase />
        <Pricing />
        <Testimonials />
        <CTA />
        <Footer />
      </div>
    </Ctx.Provider>
  )
}
